import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useSearch } from "@tanstack/solid-router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Button, buttonVariants } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, createApiQuery, SERVER_BASE_URL } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { queryKeys, sharedApi } from "@vigia/shared";
import { showToast } from "@/components/ui/toast";
import { Link2, ShieldCheck } from "@fleet/icons";

type ProviderId = "google" | "apple";

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

const PROVIDERS_STALE_TIME_MS = 5 * 60 * 1000;

const linkUrl = (provider: ProviderId) =>
  `${SERVER_BASE_URL.replace(/\/$/u, "")}/api/auth/link-social/${provider}`;

/**
 * "Connected accounts": lets you link/unlink Google and Apple from the
 * profile. Only shows providers ENABLED in the backend. It respects the
 * last-method safeguard (the backend refuses to leave the user with no access).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Reads ?linked=<provider> to show feedback after returning from the callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  // P23/CC-60 · P26. Both `queryFn` did `data ?? {…}` over
  // `openapi-fetch`, which NEVER throws: a 500 resolved to SUCCESS with a
  // FABRICATED object, the `isError` branch was DEAD code and the card lied
  // ("Google · Not connected" + "This is your only sign-in method") about an
  // account that could be linked. `createApiQuery` → `handleApiResponse`,
  // which throws on any non-2xx, like the rest of the repo.
  const providersQuery = createApiQuery(
    () => queryKeys.auth.socialProviders(),
    async () => sharedApi.socialProviders(api),
    () => ({ staleTime: PROVIDERS_STALE_TIME_MS, retry: false }),
  );

  const methodsQuery = createApiQuery(
    () => queryKeys.auth.loginMethods(),
    async () => sharedApi.loginMethods(api),
    () => ({ retry: false }),
  );

  /**
   * P23/CC-60 · P26, the third piece: without this the error box is
   * UNREACHABLE. On error `enabledProviders()` is `[]`, so the OUTER
   * `<Show>` hid the whole card —neither content nor error— and anyone looking for
   * "the error message is missing" saw nothing odd. Measured on the baseline: the
   * "Connected accounts" card appeared 0 times with `providers` down.
   */
  const hasLoadFailure = () => providersQuery.isError || methodsQuery.isError;

  const enabledProviders = createMemo<ProviderId[]>(() => {
    // P23 / CC-60 · I2: `createMemo` is EAGER, so these reads happen at
    // MOUNT, before any `<Show>`. `data` is the only field of the
    // solid-query proxy that SUSPENDS: without `isSuccess` in front, the
    // whole card waited on the network to paint its chrome.
    if (!providersQuery.isSuccess) {
      return [];
    }
    const p = providersQuery.data;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  // P23 / CC-60 · I2: without the guard, the `?? []` turned "could not ask"
  // into "none is linked" and the screen said "Not connected" for a
  // Google account that WAS linked.
  const linkedProviders = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.providers ?? []) : [],
  );
  const hasPassword = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.has_password ?? false) : false,
  );

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Number of total sign-in methods (password + each linked social).
  const totalMethods = createMemo(() => linkedProviders().length);

  // Is this the LAST method? (it cannot be unlinked without setting another).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      const { error } = await sharedApi.unlinkAccount(api, provider);
      if (error) {
        const message = error.message ?? "";
        if (message.includes("only sign-in method")) {
          showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
        } else {
          showToast(t(msg`Could not unlink the account. Please try again.`), "error");
        }
        return;
      }
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback on returning from the linking flow (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    }
  });

  return (
    <Show when={enabledProviders().length > 0 || hasLoadFailure()}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          {/*
            P23 / CC-60 · I3: «no hay respuesta» != «no hay nada». Se ENVUELVE el bloque
            entero, no se encadena: en error `isLoading`/`isPending` son `false`, asi que
            un `when={!q.isLoading && !q.isError}` con `fallback={esqueleto}` pintaria la
            caja de error Y el esqueleto a la vez, para siempre.
          */}
          <Show when={hasLoadFailure()}>
            <QueryErrorState
              testId="connected-accounts-error"
              compact
              title={t(msg`Couldn't load your connected accounts`)}
              onRetry={() => {
                // Retries ONLY the one(s) that failed.
                if (providersQuery.isError) {
                  void providersQuery.refetch();
                }
                if (methodsQuery.isError) {
                  void methodsQuery.refetch();
                }
              }}
            />
          </Show>
          <Show when={!hasLoadFailure()}>
            <Show
              when={!methodsQuery.isPending && !providersQuery.isPending}
              fallback={
                <div class="space-y-3">
                  <Skeleton class="h-14 w-full rounded-xl" />
                  <Skeleton class="h-14 w-full rounded-xl" />
                </div>
              }
            >
              <For each={enabledProviders()}>
                {(provider) => (
                  <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                    <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Link2 class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                      <Show
                        when={isLinked(provider)}
                        fallback={
                          <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                        }
                      >
                        <Badge
                          variant="secondary"
                          class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                        >
                          {t(msg`Connected`)}
                        </Badge>
                      </Show>
                    </div>

                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <a
                          href={linkUrl(provider)}
                          class={buttonVariants({ variant: "outline", size: "sm" })}
                          data-testid={`link-${provider}`}
                        >
                          {t(msg`Connect`)}
                        </a>
                      }
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy() || isLastMethod(provider)}
                        title={
                          isLastMethod(provider)
                            ? t(msg`Set a password before unlinking your only sign-in method.`)
                            : undefined
                        }
                        onClick={() => setConfirmUnlink(provider)}
                        class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        {t(msg`Disconnect`)}
                      </Button>
                    </Show>
                  </div>
                )}
              </For>

              <Show when={!hasPassword() && totalMethods() <= 1}>
                <p class="text-xs text-muted-foreground">
                  {t(
                    msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                  )}
                </p>
              </Show>
            </Show>
          </Show>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
